import instance from 'api';
import { AxiosRequestConfig } from 'axios';
import {
  IPositionMatrixRequest,
  IPositionMatrixResponse,
  IYard,
  IObjectType,
  ISelfManagedTeam,
  ISubstitutionCreateRequest,
  ISubstitutionUpdateRequest,
  IRemoveExecutorResponse,
  IReassignCandidate,
  ISubstitutionRealEstate,
} from './interfaces';

// Matrix
export const getPositionMatrix = (data: IPositionMatrixRequest) => {
  return instance()
    .post<IPositionMatrixResponse>('/executor/positionMatrix', data)
    .then((response) => response.data);
};

// Area
export const getArea = (areaId: number) => {
  return instance()
    .post('/area/load', { areaId })
    .then((response) => response.data);
};

// Real estate
export const saveAreaRealEstate = (data: {
  areaId: number;
  realEstateGuid: string;
  realEstateYardId?: number;
  realEstateObjectTypeId?: number;
}) => {
  return instance()
    .post('/area/saveAreaRealEstate', data)
    .then((response) => response.data);
};

export const removeAreaRealEstate = (areaId: number, realEstateGuid: string) => {
  return instance()
    .post('/area/removeAreaRealEstate', { areaId, realEstateGuid })
    .then((response) => response.data);
};

export const updateAreaRealEstateObjectType = (realEstateGuid: string, realEstateObjectTypeId: number | null) => {
  return instance()
    .post('/area/updateAreaRealEstateObjectType', { realEstateGuid, realEstateObjectTypeId })
    .then((response) => response.data);
};

export const moveYard = (yardId: number, fromAreaId: number, toAreaId: number) => {
  return instance()
    .post('/area/moveYard', { yardId, fromAreaId, toAreaId })
    .then((response) => response.data);
};

export const assignRealEstateToYard = (realEstateYardId: number, realEstateGuid: string) => {
  return instance()
    .post('/realEstateYard/assignRealEstate', { realEstateYardId, realEstateGuid })
    .then((response) => response.data);
};

export const moveRealEstates = (areaId: number, realEstateGuids: string[]) => {
  return instance()
    .post('/area/moveRealEstate', { areaId, realEstateGuids })
    .then((response) => response.data);
};

// Area heads
export const getAreaHeadList = (areaId: number) => {
  return instance()
    .post('/area/areaHeadList', { areaId })
    .then((response) => response.data);
};

export const saveAreaHead = (areaId: number, positionId: number, userId: string) => {
  return instance()
    .post('/area/saveAreaHead', { areaId, positionId, userId })
    .then((response) => response.data);
};

export const deleteAreaHead = (areaId: number, positionId: number, userId: string) => {
  return instance()
    .post('/area/deleteAreaHead', { areaId, positionId, userId })
    .then((response) => response.data);
};

// Positions
export const addAreaPosition = (areaId: number, positionId: number, executorLimit?: number | null) => {
  return instance()
    .post('/executor/addAreaPosition', { areaId, positionId, executorLimit })
    .then((response) => response.data);
};

export const deleteAreaPosition = (areaId: number, positionId: number) => {
  return instance()
    .post('/executor/deleteAreaPosition', { areaId, positionId })
    .then((response) => response.data);
};

// Executor assignment
export const saveRealEstatePositionExecutor = (realEstateGuid: string, positionId: number, userId: string) => {
  return instance()
    .post('/executor/saveRealEstatePositionExecutor', { realEstateGuid, positionId, userId })
    .then((response) => response.data);
};

export const deleteRealEstatePositionExecutor = (realEstateGuid: string, positionId: number, userId: string) => {
  return instance()
    .post('/executor/deleteRealEstatePositionExecutor', { realEstateGuid, positionId, userId })
    .then((response) => response.data);
};

// Executor removal with active apps check
export const removeRealEstatePositionExecutor = (data: {
  realEstateGuid: string;
  positionId: number;
  userId: string;
  reassignToUserId?: string | null;
  reassignToPositionId?: number | null;
  keepCurrentExecutor?: boolean;
}) => {
  return instance()
    .post<IRemoveExecutorResponse>('/executor/removeRealEstatePositionExecutor', data)
    .then((response) => response.data);
};

// Кандидаты на переназначение заявок: матрица ЖК, у админа — все сотрудники; список собирает бэкенд.
// Возвращается функция: AsyncSearch вызывает fetch(config) и кладёт строку поиска в config.params
export const getReassignCandidates =
  (realEstateGuid: string, excludeUserId: string) =>
  (config: AxiosRequestConfig = {}): Promise<IReassignCandidate[]> => {
    const search = config.params?.search || null;
    const { params, ...rest } = config;
    return instance()
      .post<IReassignCandidate[]>(
        '/reassignment/executorsForRealEstate',
        { realEstateGuid, excludeUserId, ...(search ? { search } : {}) },
        rest,
      )
      .then((response) => response.data);
  };

// Substitution
export const createSubstitution = (data: ISubstitutionCreateRequest) => {
  return instance()
    .post<number>('/substitution/create', data)
    .then((response) => response.data);
};

// ЖК для «Применить ко всем объектам»: где у основного эта должность на участке, кроме уже замещённых
export const getSubstitutionRealEstatesForAll = (
  realEstateGuid: string,
  positionId: number,
  originalExecutorId: string,
): Promise<ISubstitutionRealEstate[]> => {
  return instance()
    .post<ISubstitutionRealEstate[]>('/substitution/realEstatesForAll', {
      realEstateGuid,
      positionId,
      originalExecutorId,
    })
    .then((response) => response.data);
};

export const updateSubstitutionPeriod = (data: ISubstitutionUpdateRequest) => {
  return instance()
    .post('/substitution/updatePeriod', data)
    .then((response) => response.data);
};

export const returnOriginalExecutor = (substitutionId: number) => {
  return instance()
    .post('/substitution/returnOriginal', { substitutionId })
    .then((response) => response.data);
};

// Executor limit
export const setRealEstatePositionLimit = (
  realEstateGuid: string,
  positionId: number,
  executorLimit: number | null,
) => {
  return instance()
    .post('/executor/setRealEstatePositionLimit', { realEstateGuid, positionId, executorLimit })
    .then((response) => response.data);
};

// OSI
export const transferRealEstateToOsi = (data: {
  areaId: number;
  realEstateGuid: string;
  transferDate: string;
  transferReason: string;
}) => {
  return instance()
    .post('/area/transferRealEstateToOsi', data)
    .then((response) => response.data);
};

// Dictionaries
export const getPositions = () => {
  return instance()
    .post('/dictionary/adminPositions/listExecutors', {})
    .then((response) => response.data);
};

// onlyActive — без отключённых и заблокированных: закрепить их бэкенд всё равно не даст
export const getUsers = (config: AxiosRequestConfig = {}) => {
  const search = config.params?.search || null;
  const { params, ...rest } = config;
  return instance()
    .post('/users/list', search ? { search, onlyActive: true } : { onlyActive: true }, rest)
    .then((response) => response.data);
};

export const getRealEstatesByName = (config: AxiosRequestConfig = {}) => {
  const name = config.params?.search || '';
  const { params, ...rest } = config;
  return instance()
    .post(
      '/dictionary/realEstates',
      {
        name,
        onlyFromMatrix: false,
        onlyWithKeytransferDate: true,
        isNoneService: true,
        includeUnserved: false,
        isCheckListBefore: true,
      },
      rest,
    )
    .then((response) => response.data);
};

export const getYardsNotDeleted = (): Promise<IYard[]> => {
  return instance()
    .post<Array<{ realEstateYardId: number; realEstateYardName: string }>>('/realEstateYard/listAllNotDeleted')
    .then((response) => response.data.map((y) => ({ yardId: y.realEstateYardId, yardName: y.realEstateYardName })));
};

export const getObjectTypesNotDeleted = (): Promise<IObjectType[]> => {
  return instance()
    .post<Array<{ realEstateObjectTypeId: number; realEstateObjectTypeName: string }>>(
      '/realEstateObjectType/listAllNotDeleted',
    )
    .then((response) =>
      response.data.map((o) => ({
        objectTypeId: o.realEstateObjectTypeId,
        objectTypeName: o.realEstateObjectTypeName,
      })),
    );
};

export const getSelfManagedTeamsNotDeleted = (): Promise<ISelfManagedTeam[]> => {
  return instance()
    .post<Array<{ selfManagedTeamId: number; selfManagedTeamName: string }>>('/selfManagedTeam/listAllNotDeleted')
    .then((response) => response.data);
};

export const getAreas = () => {
  return instance()
    .post('/area/list', {})
    .then((response) => response.data);
};

export const saveYard = (data: { name: string }) => {
  return instance()
    .post<number>('/realEstateYard/save', data)
    .then((response) => response.data);
};
